import React, { useState, useMemo } from "react";
import { DataTable } from "@/components/ui/DataTable/index";
import { Combobox } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import {
  useListMarketZoneBreakdowns,
  useDeleteMarketZoneBreakdown,
  MarketZoneBreakdown,
} from "@/lib/api/risk/marketZoneBreakthrough";
import { useListMarketZones } from "@/lib/api/generalContract";
import { useListMinimalUsers } from "@/lib/api/users";
import { Commodities, CommodityOptions } from "@/lib/api/commodity";
import { useListCustomers } from "@/lib/api/customer";
import {
  createMarketZoneBreakdownColumns,
  MarketZoneBreakdownWithDisplay
} from "./marketZoneBreakdownColumns";
import { CreateMarketZoneBreakdownDialog } from "./CreateMarketZoneBreakdownDialog";
import { UpdateMarketZoneBreakdownDialog } from "./UpdateMarketZoneBreakdownDialog";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";
import { get_customer_commodity_top_level } from "../common";
import { formatNumberToDisplay } from "@/lib/api/common";

interface ExternalFilters {
  crop_year?: number;
  selectedCommodity?: string;
  selectedMarketZone?: string;
}

interface CustomerBreakdownViewProps {
  customer_id?: string;
  crop_year: number;
  isModal?: boolean;
  useExternalFilters?: boolean;
  externalFilters?: ExternalFilters;
  onFilterChange?: (filters: ExternalFilters) => void;
  baseTopLevelResult?: ReturnType<typeof useListRiskSummaryFull>;
}

export function CustomerBreakdownView({
  baseTopLevelResult,
  customer_id,
  crop_year,
  isModal,
  useExternalFilters = false,
  externalFilters,
  onFilterChange
}: CustomerBreakdownViewProps) {
  const { toast } = useToast();
  const [internalSelectedMarketZone, setInternalSelectedMarketZone] = useState<string>("");
  const [internalSelectedCommodity, setInternalSelectedCommodity] = useState<string>(Commodities.Corn);

  const summaryData = baseTopLevelResult?.data?.data?.summary || {};
  // Use external filters when available, otherwise use internal state
  const selectedMarketZone = useExternalFilters ? (externalFilters?.selectedMarketZone || "") : internalSelectedMarketZone;
  const selectedCommodity = useExternalFilters ? (externalFilters?.selectedCommodity || Commodities.Corn) : internalSelectedCommodity;
  const effectiveCropYear = useExternalFilters ? (externalFilters?.crop_year || crop_year) : crop_year;

  const { isReady: isSummaryReady, commodity_top_level_items } = useMemo(() => {
    if (!customer_id) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
    if (!summaryData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
    if (!summaryData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

    const customerName = customer_id ? summaryData.customer_name_map[customer_id] || "Unknown Customer" : "";

    const commodity_top_level_items = summaryData.commodity_list.map((commodity_type: Commodities) => get_customer_commodity_top_level({ commodity_type, customer_id, summaryData })).filter(Boolean).sort((a: any, b: any) => a.commodity_display_name.localeCompare(b.commodity_display_name));

    return { isReady: true, customerName, commodity_top_level_items };
  }, [summaryData]);

  const unsoldAmount = !selectedCommodity ? undefined : commodity_top_level_items.find((item: any) => item.commodity_type === selectedCommodity && item.physicalBreakdown.find((item: any) => item.id === 'unprotected_physical'))?.physicalBreakdown.find((item: any) => item.id === 'unprotected_physical') ?? undefined;



  // Filter change handlers
  const handleMarketZoneChange = (value: string) => {
    if (useExternalFilters && onFilterChange) {
      onFilterChange({ ...externalFilters, selectedMarketZone: value });
    } else {
      setInternalSelectedMarketZone(value);
    }
  };

  const handleCommodityChange = (value: string) => {
    if (useExternalFilters && onFilterChange) {
      onFilterChange({ ...externalFilters, selectedCommodity: value });
    } else {
      setInternalSelectedCommodity(value);
    }
  };
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState<MarketZoneBreakdown | null>(null);

  const deleteMarketZoneBreakdown = useDeleteMarketZoneBreakdown();

  // Fetch data
  const marketZonesQuery = useListMarketZones();
  const minimalCustomersListResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const breakdownsQuery = useListMarketZoneBreakdowns({
    customer_id: customer_id || undefined,
    market_zone_id: selectedMarketZone || undefined,
    commodity: selectedCommodity || undefined,
    crop_year: effectiveCropYear,
  });

  // Prepare options for comboboxes
  const marketZoneOptions = useMemo(() => {
    if (!marketZonesQuery.data?.data) return [];
    return marketZonesQuery.data.data
      .map((zone: { id: string; dtn_market_zone: string }) => ({
        value: zone.id,
        label: zone.dtn_market_zone,
      }));
  }, [marketZonesQuery.data]);

  const commodityOptions = useMemo(() => {
    return CommodityOptions.map(option => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  // Prepare table data with enriched display information
  const tableData = useMemo((): MarketZoneBreakdownWithDisplay[] => {
    if (!breakdownsQuery.data?.data) return [];

    const marketZoneMap = new Map(
      (marketZonesQuery.data?.data || []).map((zone: { id: string; dtn_market_zone: string }) => [zone.id, zone.dtn_market_zone])
    );

    const customerMap = new Map(
      (minimalCustomersListResult.data?.data || []).map((customer: { id: string; long_name: string }) => [customer.id, customer.long_name])
    );

    return breakdownsQuery.data.data.map((breakdown: MarketZoneBreakdown) => breakdown);
  }, [breakdownsQuery.data, marketZonesQuery.data, minimalCustomersListResult.data]);

  // Handle actions
  const handleEdit = (breakdown: MarketZoneBreakdown) => {
    setSelectedBreakdown(breakdown);
    setIsUpdateDialogOpen(true);
  };

  const handleDelete = async (breakdown: MarketZoneBreakdown) => {
    if (!window.confirm("Are you sure you want to delete this market zone breakdown?")) {
      return;
    }

    try {
      const result = await deleteMarketZoneBreakdown(breakdown.id);
      if (result.success) {
        toast({
          title: "Success",
          description: "Market zone breakdown deleted successfully",
        });
      } else {
        throw new Error("Delete failed");
      }
    } catch (error) {
      console.error("Failed to delete breakdown:", error);
      toast({
        title: "Error",
        description: "Failed to delete market zone breakdown",
        variant: "destructive",
      });
    }
  };

  const handleCreateSuccess = () => {
    breakdownsQuery.refetch();
    setIsCreateDialogOpen(false);
  };

  const handleUpdateSuccess = () => {
    breakdownsQuery.refetch();
    setIsUpdateDialogOpen(false);
    setSelectedBreakdown(null);
  };

  // Create table columns
  const columns = useMemo(() => {
    return createMarketZoneBreakdownColumns({
      showCustomerColumn: !customer_id, // Show customer column when viewing all customers
      commodity: selectedCommodity as Commodities,
      onEdit: handleEdit,
      onDelete: handleDelete,
    });
  }, [customer_id, selectedCommodity]);

  const isLoading = breakdownsQuery.isLoading || marketZonesQuery.isLoading || minimalCustomersListResult.isLoading;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">
          Market Zone {effectiveCropYear}
        </h2>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Breakdown
        </Button>
      </div>

      {/* Filters - only show when not using external filters */}
      {!useExternalFilters && (
        <div className="flex flex-col sm:flex-row gap-4 md:grid-cols-2">
          <Combobox
            options={marketZoneOptions}
            value={selectedMarketZone}
            onValueChange={handleMarketZoneChange}
            placeholder="Select market zone"
            dwimOnChange
            isModal={isModal}
          />
          <Combobox
            options={commodityOptions}
            value={selectedCommodity}
            onValueChange={handleCommodityChange}
            placeholder="Select commodity"
            dwimOnChange
            isModal={isModal}
          />

          {isSummaryReady && (
            <div className="flex flex-col md:grid gap-2">
              <p  className="text-sm font-bold">Unsold Amount</p>
              <span className="text-xs">{formatNumberToDisplay(unsoldAmount?.value, { emptyFormat: '--', zeroFormat: '--' })} ({formatNumberToDisplay(unsoldAmount?.percent, { emptyFormat: '--', zeroFormat: '--', suffix: "%" })})</span>
            </div>
          )}
        </div>
      )}

      {/* Data Table */}
      <DataTable
        className="shadow-lg"
        stickyHeaders
        tableClassName="max-h-[calc(100vh-300px)] overflow-y-scroll"
        data={tableData}
        columns={columns}
        rowClickable
        onRowClick={(row) => handleEdit(row)}
        defaultColumnVisibility={{ customer_id: !customer_id }}
        groupBy={!customer_id ? ["market_zone_id"] : undefined}
        defaultSorting={[{ id: "market_zone_id", desc: false }, { id: "customer_id", desc: false }]}
        enableColumnVisibility
        enableSorting
        enableMultiSort
        enableFiltering
        enableExport
        enableGrouping={!customer_id} // Enable grouping only when showing all customers
        filterStyle="select"
        filterMode="internal"
        emptyMessage={
          !selectedCommodity
            ? "Please select a commodity to view breakdowns"
            : breakdownsQuery.isLoading
              ? "Loading..."
              : "No market zone breakdowns found"
        }
      />

      {/* Create Dialog */}
      <CreateMarketZoneBreakdownDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
        initialData={{
          customer_id: customer_id || "",
          crop_year: effectiveCropYear,
          commodity: selectedCommodity,
          market_zone_id: selectedMarketZone,
        }}
        baseTopLevelResult={baseTopLevelResult}
      />

      {/* Update Dialog */}
      {selectedBreakdown && (
        <UpdateMarketZoneBreakdownDialog
          isOpen={isUpdateDialogOpen}
          onClose={() => {
            setIsUpdateDialogOpen(false);
            setSelectedBreakdown(null);
          }}
          onSuccess={handleUpdateSuccess}
          initialData={selectedBreakdown}
        />
      )}
    </div>
  );
}
